import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ClipboardList, Wallet, TrendingUp, PlusCircle, PiggyBank } from "lucide-react";
import CoutureLayout from "./CoutureLayout";
import { api, formatPrice } from "../../lib/api";
import { useI18n } from "../../i18n/I18nContext";

const STAGE_KEYS = ["commande", "mesures", "coupe", "couture", "finitions", "pret", "livre"];

export default function CoutureDashboard() {
    const { t } = useI18n();
    const [stats, setStats] = useState(null);

    useEffect(() => {
        api.get("/couture/stats").then((r) => setStats(r.data)).catch(() => {});
    }, []);

    const kpis = [
        { label: t("couture.kpi_orders"), value: stats?.total ?? "—", icon: ClipboardList },
        { label: t("couture.kpi_revenue"), value: stats ? formatPrice(stats.revenue_fcfa, "FCFA") : "—", icon: TrendingUp },
        { label: t("couture.kpi_deposits"), value: stats ? formatPrice(stats.deposits_fcfa, "FCFA") : "—", icon: Wallet },
        { label: t("couture.kpi_outstanding"), value: stats ? formatPrice(stats.outstanding_fcfa, "FCFA") : "—", icon: Wallet },
        { label: t("couture.kpi_profit"), value: stats ? formatPrice(stats.profit_fcfa, "FCFA") : "—", icon: PiggyBank },
    ];

    return (
        <CoutureLayout>
            <div className="p-6 md:p-10" data-testid="couture-dashboard">
                <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
                    <div>
                        <h1 className="font-display text-3xl text-brand-navy">{t("couture.dashboard")}</h1>
                        <div className="w-12 h-px bg-brand-gold mt-2" />
                    </div>
                    <Link
                        to="/couturier/commandes/nouvelle"
                        data-testid="couture-new-order-cta"
                        className="inline-flex items-center gap-2 px-5 py-3 bg-brand-gold hover:bg-brand-gold-deep text-white text-[0.72rem] tracking-[0.22em] uppercase font-semibold transition-colors"
                    >
                        <PlusCircle className="w-4 h-4" /> {t("couture.new_order")}
                    </Link>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4 mb-10">
                    {kpis.map((k, i) => (
                        <motion.div
                            key={k.label}
                            initial={{ opacity: 0, y: 14 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: i * 0.06 }}
                            className="bg-white border border-gray-100 p-5"
                            data-testid={`couture-kpi-${i}`}
                        >
                            <div className="flex items-center justify-between mb-3">
                                <span className="text-[0.62rem] tracking-[0.2em] uppercase text-gray-400">{k.label}</span>
                                <k.icon className="w-4 h-4 text-brand-gold" />
                            </div>
                            <div className="font-display text-2xl text-brand-navy">{k.value}</div>
                        </motion.div>
                    ))}
                </div>

                <h2 className="text-[0.7rem] tracking-[0.22em] uppercase font-bold text-gray-500 mb-4">
                    {t("couture.pipeline_title")}
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2" data-testid="couture-pipeline">
                    {STAGE_KEYS.map((s) => (
                        <div key={s} className="bg-brand-navy text-white p-4 text-center">
                            <div className="font-display text-xl text-brand-gold">{stats?.by_stage?.[s] ?? 0}</div>
                            <div className="text-[0.6rem] tracking-[0.16em] uppercase text-white/70 mt-1">
                                {t(`couture.stage_${s}`)}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </CoutureLayout>
    );
}
